import isEmpty from 'lodash/isEmpty'
import React, { useState } from 'react'
import { HorizontalStack, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import {
  SelectAsync,
  type SelectCreatableOption,
  UserFormValidate,
  UserMenuList,
  UserOptionSimple,
} from '@ztechapps/webui.input'
import { VendorWidget } from '../VendorWidget'
import { useVendorSelect } from '@ztechapps/vendor.common'
import type { VendorSelectProps } from '@ztechapps/webui.contract'
import { useError } from '@ztechapps/common.hook'
import get from 'lodash/get'
import { buildUserSelectLabel, extractEmail, isValidEmail } from '@ztechapps/common.utils'
import { Form } from '@ztechapps/webui.form'
import { Stack } from 'react-bootstrap'
import { FormSaveCancelButtons } from '@ztechapps/webui.form'
import { VendorFormLayout } from './VendorFormLayout'

export const VendorSelect = ({
  filter,
  showInfo = false,
  onChange,
  value: valueProp,
  As,
  ...rest
}: VendorSelectProps) => {
  const { dataLoadOptions } = useVendorSelect()
  const { onError } = useError()
  As = As || SelectAsync

  const [value, setValue] = useState(valueProp)
  const [actionMeta, setActionMeta] = React.useState(null)
  const [showAddTooltip, setShowAddTooltip] = useState(false)
  const isCreate: boolean = value?.__isCreate

  const onSubmitNewVendor = async (option) => {
    setValue(option)
    setShowAddTooltip(false)

    actionMeta.option = option
    onChange(option, actionMeta)
  }

  const tooltipBody = (
    <div style={{ minWidth: '20vw' }}>
      <Form
        onSubmit={onSubmitNewVendor}
        initialValues={value}
        validate={UserFormValidate}
        onReset={() => setShowAddTooltip(false)}
      >
        <Stack gap={2}>
          <h5>Update other information:</h5>

          <VendorFormLayout />

          <HorizontalStack className="justify-content-end">
            <FormSaveCancelButtons saveIcon=" " saveText="Update Info" />
          </HorizontalStack>
        </Stack>
      </Form>
    </div>
  )

  return (
    <React.Fragment>
      <TooltipPopOver show={showAddTooltip} tooltipBody={tooltipBody} showHeader={false} placement="bottom">
        <div>
          <As
            {...{
              components: { Option: UserOptionSimple, MenuList: UserMenuList },
              placeholder: 'Type company or email or name.',
              labelKey: 'profile.name',
              noOptionsMessage: () => 'Cannot find any vendors.',
              dataLoadOptions: dataLoadOptions({ filter }),
              getNewOptionData: (inputValue, optionLabel) => {
                const company = extractEmail(inputValue)
                return {
                  value: inputValue,
                  label: inputValue,
                  profile: {
                    name: company?.contactName ?? optionLabel,
                    email: optionLabel,
                  },
                  company: {
                    name: company?.companyName ?? 'NEW-COMPANY',
                    domain: company?.domainName,
                    _id: null, //we set this to null to automatically create from server
                    __isCreate: true,
                  },
                  _id: inputValue,
                  __isCreate: true,
                }
              },
              formatCreateLabel: (inputValue) => {
                return `Press TAB to create vendor "${inputValue} (make sure it is valid email format)"`
              },
              onChange: (newValue, actionMeta) => {
                const operate = (value: SelectCreatableOption) => {
                  if (actionMeta.action === 'create-option') {
                    const email = get(value, 'profile.email')
                    if (!isValidEmail(email)) {
                      onError(`${email} is not a valid email address.`)
                    }
                  }
                }

                //we need to consider if hte
                if (rest.isMulti) {
                  newValue.forEach((m) => operate(m))
                } else {
                  operate(newValue as SelectCreatableOption)
                }

                setValue(newValue)
                setActionMeta(actionMeta)

                //show the tooltip
                if (actionMeta.action === 'create-option') {
                  setShowAddTooltip(true)
                }

                onChange(newValue, actionMeta)
              },

              ...rest,
            }}
            value={value} //set the value
            getOptionLabel={buildUserSelectLabel}
          />
        </div>
      </TooltipPopOver>

      <ShowIf condition={showInfo && !isEmpty(value?._id) && !isCreate}>
        <div className={'py-2'}>
          <VendorWidget {...value} />
        </div>
      </ShowIf>
    </React.Fragment>
  )
}
